<script setup lang="ts">
import { computed } from 'vue'

// Fan chart for CPCV equity-curve paths, extracted verbatim from
// BacktestPanel.vue so BGA/JH and Gold render the identical graph.
// Props: the job's cpcvResult (paths[].equityCurve as number[]).
const props = defineProps<{ cpcvResult: any }>()

// Skip SVG rendering above this many total points across all paths
// rather than crashing the component with a RangeError on string join.
const CHART_POINTS_CAP = 200_000

const fanChartData = computed(() => {
  const result = props.cpcvResult
  if (!result || !result.paths || result.paths.length === 0) return null
  const paths = result.paths
  const maxLen = Math.max(...paths.map((p: any) => p.equityCurve.length))
  if (maxLen < 2) return null
  const totalPoints = paths.reduce((sum: number, p: any) => sum + p.equityCurve.length, 0)
  if (totalPoints > CHART_POINTS_CAP) {
    console.warn(`fanChartData: skipping render, ${totalPoints} points exceeds cap ${CHART_POINTS_CAP}`)
    return null
  }

  // Chart layout: 40px left margin for Y-axis labels, 600 wide, 250 tall
  const W = 600, H = 250, ML = 48

  // Normalise each path to base-100 so they all start aligned and the fan
  // shows percentage growth, not absolute NPV differences
  const normPaths = paths.map((p: any) => {
    const first = p.equityCurve[0] || 1
    return p.equityCurve.map((v: number) => (v / first) * 100)
  })

  // Global min/max on normalised values
  let min = Infinity, max = -Infinity
  for (const np of normPaths)
    for (const v of np) { if (v < min) min = v; if (v > max) max = v; }
  const range = max - min || 1

  // Helper: value -> SVG Y
  const toY = (v: number) => H - 10 - ((v - min) / range) * (H - 20)
  const toX = (i: number, len: number) => ML + (i / Math.max(1, len - 1)) * (W - ML)

  // Per-step percentiles for confidence band
  const p5Curve: number[] = [], p95Curve: number[] = [], medianCurve: number[] = []
  for (let i = 0; i < maxLen; i++) {
    const vals = normPaths.map((np: number[]) => np[i]).filter((v: number) => v !== undefined).sort((a: number, b: number) => a - b)
    if (vals.length === 0) break
    const pct = (p: number) => {
      const idx = Math.max(0, Math.ceil(vals.length * p) - 1)
      return vals[Math.min(idx, vals.length - 1)]
    }
    p5Curve.push(pct(0.05))
    p95Curve.push(pct(0.95))
    const mid = Math.floor(vals.length / 2)
    medianCurve.push(vals.length % 2 === 0 ? (vals[mid - 1] + vals[mid]) / 2 : vals[mid])
  }

  // P5/P95 confidence band as a filled polygon
  const bandPoints: string[] = []
  for (let i = 0; i < p5Curve.length; i++)
    bandPoints.push(`${toX(i, p5Curve.length).toFixed(1)},${toY(p95Curve[i]).toFixed(1)}`)
  for (let i = p5Curve.length - 1; i >= 0; i--)
    bandPoints.push(`${toX(i, p5Curve.length).toFixed(1)},${toY(p5Curve[i]).toFixed(1)}`)
  const bandPath = 'M' + bandPoints.join(' L') + ' Z'

  // Individual path SVG strings — colour by rank
  // Best path (highest final value) = blue, worst = orange, rest = grey
  const finals = normPaths.map((np: number[], idx: number) => ({ idx, val: np[np.length - 1] ?? 0 }))
  finals.sort((a: any, b: any) => b.val - a.val)
  const bestIdx = finals[0]?.idx ?? -1
  const worstIdx = finals[finals.length - 1]?.idx ?? -1

  const pathStrs = normPaths.map((np: number[]) => {
    return np.map((v: number, i: number) => {
      const x = toX(i, np.length)
      const y = toY(v)
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    }).join(' ')
  })

  const pathColours = normPaths.map((_: number[], idx: number) => {
    if (idx === bestIdx) return '#42a5f5'   // blue = best
    if (idx === worstIdx) return '#ff9800'  // orange = worst
    return '#8b949e'                        // grey = others
  })

  // Median SVG string
  const medianStr = medianCurve.map((v, i) => {
    const x = toX(i, medianCurve.length)
    const y = toY(v)
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')

  // Y-axis tick labels (normalised values → percentage labels)
  const yTicks: Array<{ y: number; label: string }> = []
  const niceStep = range < 200 ? 50 : range < 500 ? 100 : range < 2000 ? 250 : 500
  const start = Math.ceil(min / niceStep) * niceStep
  for (let v = start; v <= max; v += niceStep) {
    yTicks.push({ y: toY(v), label: v.toFixed(0) })
  }

  // X-axis labels: path equity curves don't carry dates, so show index-based
  // markers (start / end) as a minimal orientation aid
  const xLabels: Array<{ x: number; label: string }> = [
    { x: toX(0, maxLen), label: 'start' },
    { x: toX(maxLen - 1, maxLen), label: 'end' },
  ]

  return { paths: pathStrs, pathColours, median: medianStr, band: bandPath, yTicks, xLabels, W, H, ML }
})
</script>

<template>
  <div v-if="fanChartData" class="cpcv-fan-chart" data-testid="cpcv-fan-chart">
    <h4>Equity Curve Paths (normalised to 100)</h4>
    <svg :viewBox="`0 0 ${fanChartData.W} ${fanChartData.H}`" preserveAspectRatio="xMidYMid meet" class="chart-svg">
      <line v-for="t in fanChartData.yTicks" :key="'grid-'+t.label"
        :x1="fanChartData.ML" :y1="t.y" :x2="fanChartData.W" :y2="t.y"
        stroke="#30363d" stroke-width="0.5" stroke-dasharray="4,4" />
      <text v-for="t in fanChartData.yTicks" :key="'y-'+t.label"
        :x="fanChartData.ML - 6" :y="t.y + 3"
        fill="#8b949e" font-size="9" text-anchor="end">{{ t.label }}</text>
      <text v-for="xl in fanChartData.xLabels" :key="'x-'+xl.label"
        :x="xl.x" :y="fanChartData.H - 1"
        fill="#8b949e" font-size="8" text-anchor="middle">{{ xl.label }}</text>
      <path :d="fanChartData.band" fill="#3fb950" fill-opacity="0.12" stroke="none" />
      <path v-for="(d, i) in fanChartData.paths" :key="'p-'+i"
        :d="d" fill="none" :stroke="fanChartData.pathColours[i]"
        stroke-opacity="0.5" stroke-width="1" />
      <path :d="fanChartData.median" fill="none" stroke="#3fb950" stroke-width="2.5" />
      <line :x1="fanChartData.ML" :y1="0" :x2="fanChartData.ML" :y2="fanChartData.H - 10"
        stroke="#30363d" stroke-width="1" />
    </svg>
    <div class="fan-legend">
      <span class="fan-legend-item"><span class="dot" style="background:#3fb950"></span> Median</span>
      <span class="fan-legend-item"><span class="dot" style="background:#42a5f5"></span> Best path</span>
      <span class="fan-legend-item"><span class="dot" style="background:#ff9800"></span> Worst path</span>
      <span class="fan-legend-item"><span class="swatch" style="background:rgba(63,185,80,0.12);border:1px solid rgba(63,185,80,0.3)"></span> P5–P95 band</span>
    </div>
  </div>
</template>

<style scoped>
.cpcv-fan-chart { margin-top: 12px; }
.cpcv-fan-chart h4 { margin: 0.75rem 0 0.5rem; font-size: 0.85rem; color: #aaa; font-weight: normal; }
.chart-svg { width: 100%; height: auto; }
.fan-legend { display: flex; gap: 14px; margin-top: 6px; font-size: 11px; color: #8b949e; }
.fan-legend-item { display: inline-flex; align-items: center; gap: 5px; }
.fan-legend .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; }
.fan-legend .swatch { display: inline-block; width: 14px; height: 8px; }
</style>
